import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Loader2,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Calendar,
  Building2,
  MoreHorizontal,
  ThumbsUp,
  ThumbsDown,
  Trash2,
  Eye,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { Opportunity, OpportunityStatus } from '../../types/internal-opportunity.types';
import { STATUS_CONFIG, DECISION_CONFIG, PIPELINE_STATUSES } from '../../types/internal-opportunity.types';

interface OpportunitiesTableProps {
  opportunities: Opportunity[];
  isLoading?: boolean;
  selectedIds: number[];
  onSelectionChange: (ids: number[]) => void;
  onDecision?: (id: number, decision: 'go' | 'no_go') => void;
  onDelete?: (id: number) => void;
}

interface GroupedOpportunities {
  status: OpportunityStatus;
  opportunities: Opportunity[];
}

const ALL_STATUSES: OpportunityStatus[] = [
  ...PIPELINE_STATUSES,
  'won',
  'lost',
  'abandoned',
];

export function OpportunitiesTable({
  opportunities,
  isLoading,
  selectedIds,
  onSelectionChange,
  onDecision,
  onDelete,
}: OpportunitiesTableProps) {
  const [collapsedGroups, setCollapsedGroups] = useState<Set<OpportunityStatus>>(new Set());

  const groupedOpportunities = useMemo((): GroupedOpportunities[] => {
    const groups: Record<OpportunityStatus, Opportunity[]> = {} as Record<OpportunityStatus, Opportunity[]>;

    ALL_STATUSES.forEach((status) => {
      groups[status] = [];
    });

    opportunities.forEach((opp) => {
      if (groups[opp.status]) {
        groups[opp.status].push(opp);
      }
    });

    return ALL_STATUSES
      .filter((status) => groups[status].length > 0)
      .map((status) => ({
        status,
        opportunities: groups[status],
      }));
  }, [opportunities]);

  const toggleGroup = (status: OpportunityStatus) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(status)) {
        next.delete(status);
      } else {
        next.add(status);
      }
      return next;
    });
  };

  const toggleSelectAll = (groupOpportunities: Opportunity[]) => {
    const groupIds = groupOpportunities.map((o) => o.id);
    const allSelected = groupIds.every((id) => selectedIds.includes(id));

    if (allSelected) {
      onSelectionChange(selectedIds.filter((id) => !groupIds.includes(id)));
    } else {
      const newSelection = [...new Set([...selectedIds, ...groupIds])];
      onSelectionChange(newSelection);
    }
  };

  const toggleSelect = (id: number) => {
    if (selectedIds.includes(id)) {
      onSelectionChange(selectedIds.filter((i) => i !== id));
    } else {
      onSelectionChange([...selectedIds, id]);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const getDeadlineClass = (daysUntil?: number) => {
    if (daysUntil === undefined) return '';
    if (daysUntil < 0) return 'text-destructive font-medium';
    if (daysUntil <= 7) return 'text-amber-600 font-medium';
    return '';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-copper" />
      </div>
    );
  }

  if (opportunities.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Aucune opportunité trouvée.</p>
        <p className="text-sm text-muted-foreground mt-2">
          Importez des appels d'offres depuis l'onglet "Recherche BOAMP".
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {groupedOpportunities.map(({ status, opportunities: groupOpps }) => {
        const config = STATUS_CONFIG[status];
        const isCollapsed = collapsedGroups.has(status);
        const groupIds = groupOpps.map((o) => o.id);
        const allSelected = groupIds.length > 0 && groupIds.every((id) => selectedIds.includes(id));
        const someSelected = groupIds.some((id) => selectedIds.includes(id)) && !allSelected;

        return (
          <div key={status} className="border rounded-lg overflow-hidden">
            {/* Group Header */}
            <div
              className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-muted/50 ${config.bgColor}`}
              onClick={() => toggleGroup(status)}
            >
              <Checkbox
                checked={allSelected}
                ref={(el) => {
                  if (el) {
                    (el as HTMLButtonElement & { indeterminate?: boolean }).indeterminate = someSelected;
                  }
                }}
                onCheckedChange={() => toggleSelectAll(groupOpps)}
                onClick={(e) => e.stopPropagation()}
              />
              {isCollapsed ? (
                <ChevronRight className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
              <span className={`font-medium ${config.color}`}>{config.label}</span>
              <Badge variant="secondary" className="ml-auto">
                {groupOpps.length}
              </Badge>
            </div>

            {/* Group Content */}
            {!isCollapsed && (
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30">
                    <TableHead className="w-[40px]"></TableHead>
                    <TableHead className="min-w-[300px]">Titre</TableHead>
                    <TableHead className="w-[200px]">Acheteur</TableHead>
                    <TableHead className="w-[120px]">Date limite</TableHead>
                    <TableHead className="w-[100px]">Décision</TableHead>
                    <TableHead className="w-[80px]">Source</TableHead>
                    <TableHead className="w-[60px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groupOpps.map((opp) => {
                    const decisionConfig = opp.decision ? DECISION_CONFIG[opp.decision] : null;
                    const canDecide = opp.decision !== 'go' && opp.decision !== 'no_go';

                    return (
                      <TableRow
                        key={opp.id}
                        className={selectedIds.includes(opp.id) ? 'bg-copper/5' : ''}
                      >
                        <TableCell>
                          <Checkbox
                            checked={selectedIds.includes(opp.id)}
                            onCheckedChange={() => toggleSelect(opp.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <Link
                              to={`/tm/opportunities/${opp.id}`}
                              className="font-medium hover:text-copper transition-colors line-clamp-1"
                            >
                              {opp.shortTitle || opp.title}
                            </Link>
                            {opp.reference && (
                              <p className="text-xs text-muted-foreground">{opp.reference}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {opp.buyer?.name || opp.buyerName ? (
                            <div className="flex items-center gap-1.5 text-sm">
                              <Building2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                              <span className="truncate max-w-[150px]">
                                {opp.buyer?.name || opp.buyerName}
                              </span>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className={`flex items-center gap-1.5 text-sm ${getDeadlineClass(opp.daysUntilDeadline)}`}>
                            <Calendar className="h-3.5 w-3.5 shrink-0" />
                            {formatDate(opp.submissionDeadline)}
                          </div>
                        </TableCell>
                        <TableCell>
                          {decisionConfig ? (
                            <Badge className={`text-xs ${decisionConfig.color} ${decisionConfig.bgColor}`}>
                              {decisionConfig.label}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {opp.importSource && (
                              <Badge variant="outline" className="text-xs">
                                {opp.importSource.toUpperCase()}
                              </Badge>
                            )}
                            {opp.noticeNature === 'AVIS_RECTIFICATIF' && (
                              <Badge variant="outline" className="text-xs border-amber-500 bg-amber-50 text-amber-700">
                                Rectif.
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem asChild>
                                <Link to={`/tm/opportunities/${opp.id}`}>
                                  <Eye className="mr-2 h-4 w-4" />
                                  Voir les détails
                                </Link>
                              </DropdownMenuItem>
                              {opp.sourceUrl && (
                                <DropdownMenuItem asChild>
                                  <a
                                    href={opp.sourceUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <ExternalLink className="mr-2 h-4 w-4" />
                                    Voir l'avis original
                                  </a>
                                </DropdownMenuItem>
                              )}
                              {onDecision && canDecide && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() => onDecision(opp.id, 'go')}
                                    className="text-green-600"
                                  >
                                    <ThumbsUp className="mr-2 h-4 w-4" />
                                    Décision GO
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => onDecision(opp.id, 'no_go')}
                                    className="text-red-600"
                                  >
                                    <ThumbsDown className="mr-2 h-4 w-4" />
                                    Décision NO-GO
                                  </DropdownMenuItem>
                                </>
                              )}
                              {onDelete && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() => onDelete(opp.id)}
                                    className="text-destructive"
                                  >
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Supprimer
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>
        );
      })}
    </div>
  );
}
